export class Audioplayer {
    /** @type {HTMLAudioElement|null} */
    _element = null;

    /** @type {number} */
    _index = -1;

    /** @type {boolean} */
    _playing = false;

    /** @type {boolean} */
    _loaded = false;

    /** @type {Map<string, Set<Function>} */
    _listeners = new Map();

    /** @type {{ title: string, artist: string, source: string }[]} */
    _tracks = [];

    /** @type {number|null} */
    _interval = null;

    /** @type {AudioContext|null} */
    _context = null;

    /** @type {AnalyserNode|null} */
    _analyser = null;

    /** @type {Uint8Array|null} */
    _analyserData = null;

    /** @type {MediaElementAudioSourceNode|null} */
    _track = null;

    setup() {
        if (typeof window === "undefined") return;

        this._context = new AudioContext();

        if (!this._element) {
            this._element = document.createElement("audio");
            this._element.crossOrigin = window.includeApiCredentials ? 'use-credentials' : 'anonymous';
            this._element.controls = true;
            this._element.style.display = "none";
            document.body.appendChild(this._element);
        }

        this._analyser = this._context.createAnalyser();
        this._analyser.fftSize = 256;
        this._analyserData = new Uint8Array(this._analyser.frequencyBinCount);

        this._track = this._context.createMediaElementSource(this._element);
        this._track.connect(this._analyser);
        this._track.connect(this._context.destination);

        this._element.addEventListener("play", () => {
            this._playing = true;
            this._trigger("play");
        });

        this._element.addEventListener("pause", () => {
            this._playing = false;
            this._trigger("pause");
        });

        this._element.addEventListener("ended", () => {
            this.next();
        });

        this._element.addEventListener("loadedmetadata", () => {
            this._loaded = true;
            this._trigger("load", {
                duration: this._element.duration,
            });
        });

        this._element.addEventListener("timeupdate", () => {
            this._trigger("progress", {
                currentTime: this._element.currentTime,
                duration: this._element.duration,
            });
        });

        this._setupMediaSession();

        if ("mediaSession" in navigator) {
            this._interval = setInterval(() => this._setMediaSessionPositionState(), 300);
        }

        requestAnimationFrame(this._updateAnalyser.bind(this));
    }

    addEventListener(event, listener) {
        if (!this._listeners.has(event)) {
            this._listeners.set(event, new Set());
        }

        const set = this._listeners.get(event);
        set?.add(listener);
    }

    removeEventListener(event, listener) {
        const set = this._listeners.get(event);
        set?.delete(listener);
    }

    _trigger(event, details = {}) {
        const listeners = this._listeners.get(event);

        if (!listeners) {
            return;
        }

        listeners.forEach((listener) => {
            listener(details);
        });
    }

    _setupMediaSession() {
        if (!("mediaSession" in navigator)) {
            return;
        }

        navigator.mediaSession.setActionHandler("play", () => {
            this.play();
        });

        navigator.mediaSession.setActionHandler("pause", () => {
            this.pause();
        });

        navigator.mediaSession.setActionHandler("stop", () => {
            this.reset();
        });

        navigator.mediaSession.setActionHandler(
            "seekbackward",
            ({ seekOffset }) => {
                this.backward(seekOffset ?? 10);
            },
        );

        navigator.mediaSession.setActionHandler(
            "seekforward",
            ({ seekOffset }) => {
                this.foward(seekOffset ?? 10);
            },
        );

        navigator.mediaSession.setActionHandler("previoustrack", () => {
            this.previous();
        });

        navigator.mediaSession.setActionHandler("nexttrack", () => {
            this.next();
        });
    }

    _setMediaSessionMetadata() {
        if (!("mediaSession" in navigator)) {
            return;
        }

        const track = this._tracks[this._index];

        if (track) {
            navigator.mediaSession.metadata = new MediaMetadata({
                title: track.title,
                artist: track.artist,
            });
        } else {
            navigator.mediaSession.metadata = null;
        }
    }

    _setMediaSessionPositionState() {
        if (!("mediaSession" in navigator)) {
            return;
        }

        const track = this._tracks[this._index];

        if (track && this._element && this._loaded) {
            navigator.mediaSession.setPositionState({
                duration: this._element.duration ?? 0,
                playbackRate: this._element.playbackRate,
                position: this._element.currentTime,
            });
        }
    }

    _updateAnalyser() {
        if (this._analyser && this._analyserData) {
            this._analyser.getByteFrequencyData(this._analyserData);
            this._trigger("analyser", {
                data: this._analyserData,
            });
        }

        requestAnimationFrame(this._updateAnalyser.bind(this));
    }

    meta() {
        return {
            index: this._index,
            track: this._tracks[this._index] || null,
            playing: this._playing,
            loaded: this._loaded,
            duration: this._element?.duration || 0,
            currentTime: this._element?.currentTime || 0,
        };
    }

    load(tracks = []) {
        this._index = -1;
        this._tracks = tracks;
    }

    find(number) {
        if (number === null || number === undefined) return;

        const index = this._tracks.findIndex(
            (track) => track.number === number,
        );

        if (index !== -1) {
            this.select(index);
        }
    }

    play() {
        if (this._context.state === "suspended") {
            this._context.resume();
        }

        this._element.play().catch();
    }

    pause() {
        this._element.pause();
    }

    playPause() {
        if (this._index === -1) {
            return this.select(0);
        }

        if (this._element.paused) {
            this.play();
        } else {
            this.pause();
        }
    }

    backward(time) {
        this._element.currentTime = Math.max(
            0,
            this._element.currentTime - time,
        );
    }

    forward(time) {
        this._element.currentTime = Math.min(
            this._element.duration,
            this._element.currentTime + time,
        );
    }

    select(index) {
        this._index = index;

        const track = this._tracks[index];
        this._element.src = track.source;
        this._loaded = false;

        this.play();
        this._setMediaSessionMetadata();
        this._trigger("select", { index: index, track: track });

        return index;
    }

    reset() {
        this.pause();
        this._index = -1;
        this._element.src = "";
        this._setMediaSessionMetadata();
        this._trigger("reset");
    }

    next() {
        if (this._index === -1) {
            return this.select(0);
        }

        const index = Math.min(this._tracks.length - 1, this._index + 1);

        if (index !== this._index) {
            return this.select(index);
        }
    }

    previous() {
        const index = Math.max(0, this._index - 1);

        if (index !== this._index) {
            return this.select(index);
        }
    }

    getCurrentTrack() {
        return this._tracks[this._index] || null;
    }
}
